import { useRef, useEffect } from 'react';
import { useDarkMode } from '@/hooks/useDarkMode';
import { getArchetypeAccent, getArchetypeTextAccent } from '@/config/archetypes';

/**
 * ArchetypeCarousel
 *
 * Horizontal swipeable rail of gift archetype tiles that acts as the primary
 * discovery driver on the Browse page. Accepts a pre-filtered `archetypes`
 * list (enabled only) from Browse.jsx so hydration can flow from the backend.
 */
export default function ArchetypeCarousel({ archetypes = [], activeId, onSelect }) {
  const darkMode = useDarkMode();
  const scrollRef = useRef(null);
  const activeTileRef = useRef(null);

  // On mount / active change, scroll the active tile into view smoothly.
  useEffect(() => {
    if (!activeTileRef.current || !scrollRef.current) return;
    const tile = activeTileRef.current;
    const container = scrollRef.current;
    const tileLeft = tile.offsetLeft;
    const tileWidth = tile.offsetWidth;
    const containerWidth = container.offsetWidth;
    const targetScroll = tileLeft - (containerWidth - tileWidth) / 2;
    container.scrollTo({ left: Math.max(0, targetScroll), behavior: 'smooth' });
  }, [activeId, archetypes.length]);

  if (archetypes.length <= 1) return null; // Nothing to swipe between

  const isDark = darkMode.isDark;

  return (
    <div
      className="relative w-full overflow-x-auto scroll-smooth pb-1"
      data-testid="archetype-carousel"
      role="tablist"
      aria-label="Gift archetype worlds"
      ref={scrollRef}
      style={{
        scrollSnapType: 'x mandatory',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <style>{`
        [data-testid="archetype-carousel"]::-webkit-scrollbar { display: none; }
      `}</style>

      {/* Inner track sizes to its intrinsic width so `mx-auto` centers
          the tile row when the viewport is wide enough, and falls back
          to natural left-anchored scroll on narrow viewports. */}
      <div className="flex gap-2.5 sm:gap-3 mx-auto w-fit">
        {archetypes.map((a) => {
          const isActive = activeId === a.slug;
          const accent = getArchetypeAccent(a, isDark);
          const textAccent = getArchetypeTextAccent(a, isDark);

          const inactiveBg = isDark
            ? 'rgba(255,255,255,0.04)'
            : 'rgba(0,0,0,0.02)';
          const inactiveBorder = isDark
            ? 'rgba(255,255,255,0.12)'
            : 'rgba(0,0,0,0.08)';

          return (
            <button
              key={a.slug}
              ref={isActive ? activeTileRef : null}
              onClick={() => onSelect(a.slug)}
              role="tab"
              aria-selected={isActive}
              data-testid={`archetype-tile-${a.slug}`}
              className="flex-shrink-0 flex flex-col items-start justify-center text-left rounded-2xl transition-all"
              style={{
                scrollSnapAlign: 'start',
                minWidth: '150px',
                padding: '10px 16px',
                background: isActive
                  ? isDark
                    ? `${accent}33`
                    : `${accent}30`
                  : inactiveBg,
                border: `2px solid ${isActive ? accent : inactiveBorder}`,
                boxShadow: isActive
                  ? `0 0 0 3px ${accent}33, 0 4px 12px ${accent}33`
                  : 'none',
                transform: isActive ? 'translateY(-1px)' : 'translateY(0)',
              }}
            >
              <span
                className="text-[10px] uppercase tracking-[0.14em] font-semibold mb-0.5"
                style={{
                  color: isActive
                    ? textAccent
                    : isDark
                      ? 'rgba(255,255,255,0.55)'
                      : 'rgba(0,0,0,0.5)',
                }}
              >
                {a.subtitle}
              </span>
              <span
                className="whitespace-nowrap"
                style={{
                  fontFamily: a.fontFamily,
                  ...a.titleStyle,
                  fontSize: '18px',
                  lineHeight: 1.15,
                  color: isActive
                    ? isDark
                      ? '#FFFAF5'
                      : '#20212B'
                    : isDark
                      ? 'rgba(255,250,245,0.85)'
                      : 'rgba(32,33,43,0.78)',
                }}
              >
                {a.title}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
